<template>
  <section class="settings-section section-block">
    <SectionTitle
      cyBtn="edit-reply-settings"
      :title="displayData.title"
      :btn="displayData.btn.edit"
      @edit="modal.edit = true"
    />
    <div>
      <BaseElForm label-position="left" label-width="280px">
        <!-- 預約服務開始前回覆 -->
        <BaseElFormItem class="settings-item">
          <template slot="label">
            <div class="flex items-center gap-[8px]">
              <span>{{ displayData.enableReply.label }}</span>
              <TipInfo :size="16">
                {{ displayData.enableReply.tooltip }}
              </TipInfo>
            </div>
          </template>
          <span class="content">{{ displayData.enableReply.value }}</span>
        </BaseElFormItem>

        <!-- 回覆開始時間 -->
        <BaseElFormItem class="settings-item">
          <template slot="label">
            <div class="flex items-center gap-[8px] font-normal">
              <span>{{ displayData.replyStartTime.label }}</span>
              <TipInfo :size="16">
                {{ displayData.replyStartTime.tooltip }}
              </TipInfo>
            </div>
          </template>
          <span class="content">{{ displayData.replyStartTime.value }}</span>
        </BaseElFormItem>

        <!-- 回覆截止時間 -->
        <BaseElFormItem class="settings-item">
          <template slot="label">
            <div class="flex items-center gap-[8px] font-normal">
              <span>{{ displayData.replyEndTime.label }}</span>
              <TipInfo :size="16">
                {{ displayData.replyEndTime.tooltip }}
              </TipInfo>
            </div>
          </template>
          <span class="content">{{ displayData.replyEndTime.value }}</span>
        </BaseElFormItem>

        <!-- 逾時未回覆自動取消預約 -->
        <BaseElFormItem class="settings-item">
          <template slot="label">
            <div class="flex items-center gap-[8px]">
              <span>{{ displayData.enableNoReplyAutoCancel.label }}</span>
              <TipInfo :size="16">
                {{ displayData.enableNoReplyAutoCancel.tooltip }}
              </TipInfo>
            </div>
          </template>
          <span class="content">{{ displayData.enableNoReplyAutoCancel.value }}</span>
        </BaseElFormItem>

        <!-- 自動取消時間 -->
        <BaseElFormItem class="settings-item">
          <template slot="label">
            <div class="flex items-center gap-[8px] font-normal">
              <span>{{ displayData.autoCancelTime.label }}</span>
            </div>
          </template>
          <span class="content">{{ displayData.autoCancelTime.value }}</span>
        </BaseElFormItem>
      </BaseElForm>
    </div>

    <!-- 編輯彈窗 - TODO: 完整表單功能待實作 -->
    <EditReservationReplySettingsModal
      v-if="modal.edit"
      data-testid="edit-reservation-reply-settings-modal"
      :configData="configData"
      @close="modal.edit = false"
      @refresh="$emit('refresh')"
    />
  </section>
</template>

<script>
import { defineComponent, reactive, computed } from 'vue'
import EditReservationReplySettingsModal from './EditReservationReplySettingsModal.vue'
import TipInfo from '@/components/TipInfo.vue'
import { i18n } from '@/plugins/i18n/i18n'
import { get, isNull, isUndefined } from 'lodash'

export default defineComponent({
  name: 'ReservationReplySettings',
  components: {
    EditReservationReplySettingsModal,
    TipInfo,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props) {
    const modal = reactive({
      edit: false,
    })

    // 格式化時間顯示的輔助函式
    const displayTimeValue = (config, formatKey) => {
      // 檢查 config 物件是否有效
      if (!config || isNull(config) || typeof config !== 'object') {
        return i18n.t('common.notSetupYet.text')
      }

      const { offsetUnit, offsetUnitType } = config

      if (isNull(offsetUnit) || isUndefined(offsetUnit) ||
          isNull(offsetUnitType) || isUndefined(offsetUnitType)) {
        return i18n.t('common.notSetupYet.text')
      }

      // 單位映射
      const unitMap = {
        minute: i18n.t('common.timeUnit.minute'),
        hour: i18n.t('common.timeUnit.hour'),
        day: i18n.t('common.timeUnit.day'),
      }

      const unit = unitMap[offsetUnitType] || offsetUnitType

      // 根據不同的格式鍵返回不同格式
      return i18n.t(`reservationReplySettings.displayFormat.${formatKey}`, {
        time: offsetUnit,
        unit,
      })
    }

    const displayData = computed(() => {
      const data = {
        title: i18n.t('reservationReplySettings.title'),
        btn: {
          edit: i18n.t('common.button.edit.text'),
        },
        enableReply: {
          label: i18n.t('reservationReplySettings.enableReply.label'),
          tooltip: i18n.t('reservationReplySettings.enableReply.tooltip'),
          value: get(props.configData, 'enableReply')
            ? i18n.t('common.open.text')
            : i18n.t('common.close.text'),
        },
        replyStartTime: {
          label: i18n.t('reservationReplySettings.replyStartTime.label'),
          tooltip: i18n.t('reservationReplySettings.replyStartTime.tooltip'),
          value: displayTimeValue(
            get(props.configData, 'replyStartConfig'),
            'start',
          ),
        },
        replyEndTime: {
          label: i18n.t('reservationReplySettings.replyEndTime.label'),
          tooltip: i18n.t('reservationReplySettings.replyEndTime.tooltip'),
          value: displayTimeValue(
            get(props.configData, 'replyEndConfig'),
            'end',
          ),
        },
        enableNoReplyAutoCancel: {
          label: i18n.t('reservationReplySettings.enableNoReplyAutoCancel.label'),
          tooltip: i18n.t('reservationReplySettings.enableNoReplyAutoCancel.tooltip'),
          value: get(props.configData, 'enableNoReplyAutoCancel')
            ? i18n.t('common.open.text')
            : i18n.t('common.close.text'),
        },
        autoCancelTime: {
          label: i18n.t('reservationReplySettings.autoCancelTime.label'),
          value: displayTimeValue(
            get(props.configData, 'noReplyAutoCancelConfig'),
            'cancel',
          ),
        },
      }

      return data
    })

    return {
      modal,
      displayData,
    }
  },
})
</script>

<style lang="postcss" scoped></style>
